import { useState, useRef } from "react";
import { toastService } from "@services/ToastService";
import { chatService } from "@services/ChatService";
import { useParams } from "react-router-dom";

export const useFeedback = (chatSessionId: string, reportFeedback = false) => {
  // Inside useFeedback hook
  const { reportId } = useParams<{ reportId: string }>();
  const chatLogRef = useRef<HTMLDivElement>(null);

  const [feedbackText, setFeedbackText] = useState<string>("");
  const [feedbackOption, setFeedbackOption] = useState<string>("");
  const [feedbackSubmitted, setFeedbackSubmitted] = useState<boolean>(false);

  const [feedbackForIndex, setFeedbackForIndex] = useState<number | null>(null);
  const [feedbackState, setFeedbackState] = useState<{
    type: "like" | "dislike" | null;
    showInput: boolean;
  }>({ type: null, showInput: false });

  const [feedbackSubmittedFor, setFeedbackSubmittedFor] = useState<{
    [index: number]: string;
  }>({});
  const [selectedThumbs, setSelectedThumbs] = useState<{
    [index: number]: "like" | "dislike" | null;
  }>({});
  const [feedbackHiddenFor, setFeedbackHiddenFor] = useState<{
    [index: number]: boolean;
  }>({});
  const [feedbackUpdating, setFeedbackUpdating] = useState(false);

  const scrollToBottom = () => {
    const el = chatLogRef.current;
    if (el) {
      requestAnimationFrame(() => {
        el.scrollTo({
          top: el.scrollHeight,
          behavior: "smooth",
        });
      });
    }
  };

  const handleThumbClick = (index: number, type: "like" | "dislike") => {
    if (feedbackSubmitted) return;

    const currentThumb = selectedThumbs[index];
    const isSameThumb = currentThumb === type;
    scrollToBottom();

    if (isSameThumb) {
      setSelectedThumbs((prev) => ({ ...prev, [index]: null }));
      setFeedbackForIndex(null);
      setFeedbackState({ type: null, showInput: false });
      return;
    }

    setSelectedThumbs((prev) => ({ ...prev, [index]: type }));
    setFeedbackForIndex(index);
    setFeedbackState({ type, showInput: false });
  };

  const handleFeedbackSubmit = async (index: number, feedback: string) => {
    if (feedbackSubmitted) return;

    try {
      const baseData = {
        feedback,
        feedback_type:
          selectedThumbs[index] === "like" ? "positive" : "negative",
      };

      const data = reportFeedback
        ? {
            ...baseData,
            report_id: reportId ?? "",
          }
        : {
            ...baseData,
            chat_session_id: chatSessionId ?? "",
          };

      setFeedbackUpdating(true);

      const type = reportFeedback ? "report" : "chat";
      await chatService.submitFeedback(data, type);

      setFeedbackSubmitted(true);
      setFeedbackSubmittedFor((prev) => ({ ...prev, [index]: feedback }));
      setTimeout(() => {
        setFeedbackHiddenFor((prev) => ({ ...prev, [index]: true }));
        setFeedbackForIndex(null);
        setFeedbackState({ type: null, showInput: false });
      }, 3000);
      setFeedbackText("");
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      toastService.error("Failed to submit feedback");
    } finally {
      setTimeout(() => {
        setFeedbackSubmitted(false);
        setFeedbackUpdating(false);
        setFeedbackOption("");
      }, 200);
    }
  };

  const resetFeedbackState = () => {
    setFeedbackText("");
    setFeedbackOption("");
    setFeedbackSubmitted(false);
    setFeedbackForIndex(null);
    setFeedbackState({ type: null, showInput: false });
    setFeedbackSubmittedFor({});
    setSelectedThumbs({});
    setFeedbackHiddenFor({});
    setFeedbackUpdating(false);
  };

  return {
    feedbackText,
    setFeedbackText,
    feedbackOption,
    setFeedbackOption,
    feedbackState,
    setFeedbackState,
    feedbackSubmittedFor,
    selectedThumbs,
    feedbackHiddenFor,
    feedbackUpdating,
    handleThumbClick,
    handleFeedbackSubmit,
    feedbackForIndex,
    chatLogRef,
    scrollToBottom,
    feedbackSubmitted,
    resetFeedbackState,
  };
};
